/**
 * Single-source format validators shared by backend Zod refines and frontend
 * Mantine form validators.
 *
 * The boolean `isValid*` predicates ARE the format rule; the `validate*`
 * helpers wrap them in the Mantine validator contract (`string | null`) with
 * user-facing messages. Both sides must consume these — a phone/email format
 * rule defined anywhere else can drift, which is how array-row field errors
 * ended up passing the client and bouncing off the server (US-080 family).
 */

/**
 * Zod 4's "practical email validation" regex, copied verbatim from
 * `zod/v4/core/regexes.ts` so that backend refines on `isValidEmail` accept
 * exactly what `z.string().email()` accepted before the switch. Keep in sync
 * with the pinned zod major — do not edit independently.
 */
export const EMAIL_REGEX =
  /^(?!\.)(?!.*\.\.)([A-Za-z0-9_'+\-.]*)[A-Za-z0-9_+-]@([A-Za-z0-9][A-Za-z0-9-]*\.)+[A-Za-z]{2,}$/;

export function isValidEmail(value: string): boolean {
  return EMAIL_REGEX.test(value);
}

/**
 * NANP digits after stripping formatting and an optional leading country
 * code 1 (`+1 (555) 123-4567` and `5551234567` both yield `5551234567`).
 */
function nanpLocalDigits(value: string): string {
  const digits = value.replace(/\D/g, "");
  return digits.length === 11 && digits.startsWith("1")
    ? digits.slice(1)
    : digits;
}

/**
 * US/CA (NANP) phone rule: any formatting accepted, digits must be a 10-digit
 * number with a valid area code (2–9), optionally prefixed with country
 * code 1. Exchange-code validity (second group 2–9) is deliberately not
 * enforced — legacy rows predate the rule.
 */
export function isValidPhone(value: string): boolean {
  const local = nanpLocalDigits(value);
  return local.length === 10 && local[0] !== "0" && local[0] !== "1";
}

/**
 * Mantine-contract email validator. Empty values pass — pair with a required
 * check where the field is mandatory.
 */
export function validateEmail(value: string | null | undefined): string | null {
  if (!value) return null;
  return isValidEmail(value) ? null : "Please enter a valid email address";
}

/**
 * Mantine-contract phone validator — same predicate the backend refines use,
 * split only to produce a per-cause message.
 */
export function validatePhone(value: string | null | undefined): string | null {
  if (!value) return null;
  const local = nanpLocalDigits(value);
  if (local.length !== 10) {
    return "Please enter a valid 10-digit phone number";
  }
  if (local[0] === "0" || local[0] === "1") {
    return "Area code can't start with 0 or 1";
  }
  return null;
}
